import { Menu, MenuItem, MenuItemProps } from '@mui/material';
import React, { useState } from 'react';

export interface NestedMenuItemProps extends MenuItemProps {
  /**
   * Label for the menu item
   */
  label: React.ReactNode;
  /**
   * Children to render in the submenu
   */
  children: React.ReactNode;
  /**
   * Whether the parent menu is open (required for proper behavior)
   */
  parentMenuOpen: boolean;
  /**
   * Callback when submenu should close (e.g., when parent menu closes)
   */
  onClose?: () => void;
  /**
   * Callback when this submenu opens (useful for closing other submenus)
   */
  onOpen?: () => void;
  /**
   * Anchor origin for the submenu
   */
  anchorOrigin?: {
    vertical: 'top' | 'bottom' | 'center';
    horizontal: 'left' | 'right' | 'center';
  };
  /**
   * Transform origin for the submenu
   */
  transformOrigin?: {
    vertical: 'top' | 'bottom' | 'center';
    horizontal: 'left' | 'right' | 'center';
  };
}

/**
 * A reusable nested menu item component that opens a submenu on click.
 * The submenu stays open when hovering over it and closes when clicking outside or on another item.
 */
const NestedMenuItem: React.FC<NestedMenuItemProps> = ({
  label,
  children,
  parentMenuOpen,
  onClose,
  onOpen,
  anchorOrigin = {
    vertical: 'top',
    horizontal: 'left',
  },
  transformOrigin = {
    vertical: 'top',
    horizontal: 'right',
  },
  ...menuItemProps
}) => {
  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const handleItemClick = (event: React.MouseEvent<HTMLLIElement>) => {
    event.stopPropagation();

    // Toggle submenu on click
    if (isSubMenuOpen) {
      setIsSubMenuOpen(false);
      setMenuAnchor(null);
    } else {
      setIsSubMenuOpen(true);
      setMenuAnchor(event.currentTarget);
      onOpen?.();
    }

    // Call original onClick if provided
    if (menuItemProps.onClick) {
      menuItemProps.onClick(event as any);
    }
  };

  const handleMenuClose = () => {
    setIsSubMenuOpen(false);
    setMenuAnchor(null);
    onClose?.();
  };

  return (
    <>
      <MenuItem
        {...menuItemProps}
        onClick={handleItemClick}
        selected={isSubMenuOpen && parentMenuOpen}
      >
        {label}
      </MenuItem>
      <Menu
        anchorEl={menuAnchor}
        open={isSubMenuOpen && parentMenuOpen}
        onClose={handleMenuClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
      >
        {children}
      </Menu>
    </>
  );
};

export default NestedMenuItem;
